import React, { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { LoginPage } from './components/LoginPage';
import { DnsTab } from './components/DnsTab';
import { SessionsView } from './components/SessionsView';

const API_URL = process.env.REACT_APP_API_URL || '';
export type Platform = 'whatsapp' | 'signal';

export interface ConnectionState {
    whatsapp: boolean;
    signal: boolean;
    signalNumber: string | null;
    signalApiAvailable: boolean;
    signalQrImage: string | null;
    whatsappQr: string | null;
    pairingCode: string | null;
}

// Socket créé dynamiquement après auth
let _socket: Socket | null = null;
export function getSocket(): Socket {
    return _socket!;
}

// Exporter le socket pour les composants existants
export let socket: Socket = null as any;

function createSocket(token: string): Socket {
    const s = io(API_URL || window.location.origin, {
        autoConnect: false,
        auth: { token },
        withCredentials: true,
    });
    _socket = s;
    socket = s;
    return s;
}

function App() {
    const [authToken, setAuthToken] = useState<string | null>(() => {
        return localStorage.getItem('auth_token');
    });
    const [authChecked, setAuthChecked] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionState, setConnectionState] = useState<ConnectionState>({
        whatsapp: false, signal: false, signalNumber: null,
        signalApiAvailable: false, signalQrImage: null, whatsappQr: null, pairingCode: null
    });
    const [activeTab, setActiveTab] = useState<'tracker' | 'dns' | 'sessions'>('tracker');

    // Vérifier le token au démarrage
    useEffect(() => {
        if (!authToken) { setAuthChecked(true); return; }
        fetch('/api/me', { credentials: 'include' })
            .then(r => { if (!r.ok) { setAuthToken(null); localStorage.removeItem('auth_token'); } })
            .catch(() => {})
            .finally(() => setAuthChecked(true));
    }, []);

    // Connecter le socket quand le token est validé
    useEffect(() => {
        if (!authToken || !authChecked) return;
        const s = createSocket(authToken);

        s.on('connect', () => setIsConnected(true));
        s.on('disconnect', () => {
            setIsConnected(false);
            setConnectionState(p => ({ ...p, whatsapp: false, signal: false, whatsappQr: null }));
        });
        s.on('qr', (qr: string) => setConnectionState(p => ({ ...p, whatsappQr: qr })));
        s.on('pairing-code', (code: string) => setConnectionState(p => ({ ...p, pairingCode: code })));
        s.on('connection-open', () => setConnectionState(p => ({ ...p, whatsapp: true, whatsappQr: null, pairingCode: null })));
        s.on('signal-connection-open', (d: any) => setConnectionState(p => ({ ...p, signal: true, signalNumber: d.number })));
        s.on('signal-disconnected', () => setConnectionState(p => ({ ...p, signal: false, signalNumber: null })));
        s.on('signal-api-status', (d: any) => setConnectionState(p => ({ ...p, signalApiAvailable: d.available })));
        s.on('signal-qr-image', (url: string) => setConnectionState(p => ({ ...p, signalQrImage: url })));

        s.connect();
        return () => { s.disconnect(); };
    }, [authToken, authChecked]);

    const handleLogin = (token: string) => {
        localStorage.setItem('auth_token', token);
        setAuthToken(token);
    };

    const handleLogout = async () => {
        await fetch('/api/logout', { method: 'POST', credentials: 'include' });
        localStorage.removeItem('auth_token');
        socket?.disconnect();
        setAuthToken(null);
    };

    if (!authChecked) return null;
    if (!authToken) return <LoginPage onLogin={handleLogin} />;

    const isAnyPlatformReady = connectionState.whatsapp || connectionState.signal;

    const tabStyle = (active: boolean) => ({
        padding: '8px 20px', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer',
        fontWeight: 600, fontSize: 14,
        background: active ? '#ffffff' : 'transparent',
        color: active ? '#1e293b' : '#94a3b8',
        borderBottom: active ? '2px solid #6366f1' : '2px solid transparent',
        transition: 'all 0.15s',
    } as React.CSSProperties);

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
                <header className="mb-6 flex flex-wrap justify-between items-center gap-3">
                    <h1 className="text-3xl font-bold text-gray-900">📡 Activity Tracker</h1>
                    <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-sm text-gray-600">{isConnected ? 'Connecté' : 'Déconnecté'}</span>
                        {isConnected && (
                            <>
                                <span className="text-gray-300">|</span>
                                <span className={`text-sm font-medium ${connectionState.whatsapp ? 'text-green-600' : 'text-yellow-500'}`}>
                                    WA {connectionState.whatsapp ? '✓' : '○'}
                                </span>
                                <span className={`text-sm font-medium ${connectionState.signal ? 'text-blue-600' : 'text-gray-400'}`}>
                                    Signal {connectionState.signal ? '✓' : '○'}
                                </span>
                            </>
                        )}
                        <button onClick={handleLogout}
                            className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 transition-colors">
                            Déconnexion
                        </button>
                    </div>
                </header>

                {/* Onglets */}
                <div style={{ display: 'flex', gap: 4, marginBottom: -1, paddingLeft: 4 }}>
                    <button style={tabStyle(activeTab === 'tracker')} onClick={() => setActiveTab('tracker')}>
                        📡 Tracker WA/Signal
                    </button>
                    <button style={tabStyle(activeTab === 'sessions')} onClick={() => setActiveTab('sessions')}>
                        📶 Présence
                    </button>
                    <button style={tabStyle(activeTab === 'dns')} onClick={() => setActiveTab('dns')}>
                        🌐 Réseau DNS
                    </button>
                </div>

                <main style={{ background: '#f3f4f6', borderRadius: '0 12px 12px 12px', padding: '20px 0', minHeight: 400 }}>
                    {activeTab === 'tracker'
                        ? (!isAnyPlatformReady
                            ? <div className="px-4"><Login connectionState={connectionState} /></div>
                            : <div className="px-4"><Dashboard connectionState={connectionState} /></div>)
                        : activeTab === 'sessions'
                        ? <div className="px-4"><SessionsView /></div>
                        : <div className="px-4"><DnsTab /></div>
                    }
                </main>
            </div>
        </div>
    );
}

export default App;
